import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Goal,
  AlertTriangle,
  Plus,
  X,
  Ban,
  Users,
  Pencil,
} from 'lucide-react';

const EditReportDialog = ({
  editingMatch,
  clubs,
  editHomePlayers,
  editAwayPlayers,
  editForm,
  setEditForm,
  editLoading,
  isItalian,
  onSave,
  onClose,
}) => {
  const getClub = (clubId) => clubs.find((c) => c.club_id === clubId);

  const addEditEvent = (type) => {
    setEditForm(prev => ({ ...prev, events: [...prev.events, { player_id: '', event_type: type, minute: 1 }] }));
  };

  return (
    <Dialog open={!!editingMatch} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[#0a0d15] border-[#00F0FF]/20 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
            <Pencil size={20} className="text-[#00F0FF]" />
            {isItalian ? 'Modifica Report' : 'Edit Report'}
          </DialogTitle>
        </DialogHeader>
        {editingMatch && (
          <div className="space-y-5 mt-4">
            {/* Score editing */}
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                <p className="text-sm text-slate-400 mb-2 truncate">{getClub(editingMatch.home_club_id)?.name}</p>
                <Input
                  type="number" min={0}
                  value={editForm.home_score}
                  onChange={e => setEditForm(prev => ({ ...prev, home_score: parseInt(e.target.value) || 0 }))}
                  className="bg-[#050508] border-white/10 text-white text-center font-mono text-2xl"
                  data-testid="edit-home-score"
                />
              </div>
              <div className="text-center text-2xl font-bold text-slate-500">-</div>
              <div className="text-center">
                <p className="text-sm text-slate-400 mb-2 truncate">{getClub(editingMatch.away_club_id)?.name}</p>
                <Input
                  type="number" min={0}
                  value={editForm.away_score}
                  onChange={e => setEditForm(prev => ({ ...prev, away_score: parseInt(e.target.value) || 0 }))}
                  className="bg-[#050508] border-white/10 text-white text-center font-mono text-2xl"
                  data-testid="edit-away-score"
                />
              </div>
            </div>

            {/* Events editing */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                  {isItalian ? 'Eventi Partita' : 'Match Events'}
                </Label>
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" size="sm" variant="outline" className="text-[#00E676] border-[#00E676]/30 hover:bg-[#00E676]/10" onClick={() => addEditEvent('goal')} data-testid="edit-add-goal">
                    <Goal size={14} className="mr-1" /> {isItalian ? 'Gol' : 'Goal'}
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#00F0FF] border-[#00F0FF]/30 hover:bg-[#00F0FF]/10" onClick={() => addEditEvent('assist')} data-testid="edit-add-assist">
                    <Plus size={14} className="mr-1" /> Assist
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#FFEA00] border-[#FFEA00]/30 hover:bg-[#FFEA00]/10" onClick={() => addEditEvent('yellow_card')} data-testid="edit-add-yellow">
                    <AlertTriangle size={14} className="mr-1" /> {isItalian ? 'Giallo' : 'Yellow'}
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#FF1744] border-[#FF1744]/30 hover:bg-[#FF1744]/10" onClick={() => addEditEvent('red_card')} data-testid="edit-add-red">
                    <Ban size={14} className="mr-1" /> {isItalian ? 'Rosso' : 'Red'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {editForm.events.map((event, index) => {
                  const editHomeClub = getClub(editingMatch.home_club_id);
                  const editAwayClub = getClub(editingMatch.away_club_id);
                  return (
                    <div key={index} className="flex items-center gap-2 bg-[#050508] border border-white/10 p-2 rounded">
                      <Select
                        value={event.player_id}
                        onValueChange={(val) => setEditForm(prev => ({
                          ...prev,
                          events: prev.events.map((e, i) => i === index ? { ...e, player_id: val } : e)
                        }))}
                      >
                        <SelectTrigger className="flex-1 bg-transparent border-0 text-white" data-testid={`edit-event-player-${index}`}>
                          <SelectValue placeholder={event.player_name || (isItalian ? 'Seleziona giocatore' : 'Select player')} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0d15] border-white/10 max-h-60">
                          {editHomePlayers.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-bold text-[#00F0FF] uppercase flex items-center gap-2 border-b border-white/10">
                                <Users size={12} /> {editHomeClub?.name || 'Casa'}
                              </div>
                              {editHomePlayers.map(p => (
                                <SelectItem key={p.user_id} value={p.user_id}>{p.gamertag || p.name}</SelectItem>
                              ))}
                            </>
                          )}
                          {editAwayPlayers.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-bold text-[#FF1744] uppercase flex items-center gap-2 border-b border-white/10 mt-2">
                                <Users size={12} /> {editAwayClub?.name || 'Ospiti'}
                              </div>
                              {editAwayPlayers.map(p => (
                                <SelectItem key={p.user_id} value={p.user_id}>{p.gamertag || p.name}</SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <span className={`px-2 py-1 text-xs rounded flex-shrink-0 ${
                        event.event_type === 'goal' ? 'bg-[#00E676]/20 text-[#00E676]'
                        : event.event_type === 'assist' ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                        : event.event_type === 'yellow_card' ? 'bg-[#FFEA00]/20 text-[#FFEA00]'
                        : 'bg-[#FF1744]/20 text-[#FF1744]'
                      }`}>
                        {event.event_type === 'goal' && (isItalian ? 'Gol' : 'Goal')}
                        {event.event_type === 'assist' && 'Assist'}
                        {event.event_type === 'yellow_card' && (isItalian ? 'Giallo' : 'Yellow')}
                        {event.event_type === 'red_card' && (isItalian ? 'Rosso' : 'Red')}
                      </span>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Input
                          type="number" min={1} max={120}
                          value={event.minute}
                          onChange={e => setEditForm(prev => ({
                            ...prev,
                            events: prev.events.map((ev, i) => i === index ? { ...ev, minute: parseInt(e.target.value) || 1 } : ev)
                          }))}
                          className="w-14 bg-transparent border-0 text-white text-center font-mono"
                          placeholder="--"
                        />
                        <span className="text-[10px] text-slate-500 font-mono">min</span>
                      </div>
                      <Button type="button" size="icon" variant="ghost"
                        onClick={() => setEditForm(prev => ({ ...prev, events: prev.events.filter((_, i) => i !== index) }))}
                        className="text-slate-500 hover:text-[#FF1744]"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  );
                })}
                {editForm.events.length === 0 && (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    {isItalian ? 'Nessun evento. Aggiungi gol, assist o cartellini.' : 'No events. Add goals, assists or cards.'}
                  </div>
                )}
              </div>
            </div>

            {/* Save button */}
            <Button
              className="w-full bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold uppercase tracking-wider"
              onClick={onSave}
              disabled={editLoading}
              data-testid="save-edit-report"
            >
              {editLoading ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  {isItalian ? 'Salvataggio...' : 'Saving...'}
                </span>
              ) : (
                isItalian ? 'Salva Modifiche' : 'Save Changes'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditReportDialog;
